import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { identifyUser, resetUser, trackPageView, trackEvent } from '@/lib/posthog';
import { captureUTMParams, getStoredUTMParams } from '@/lib/utm';

/**
 * Handles automatic PostHog tracking:
 * - Page views on every route change
 * - User identification when logged in (with UTM attribution)
 * - User reset on logout
 * - Key lifecycle events
 */
export default function PostHogTracker() {
  const location = useLocation();
  const { user } = useAuth();
  const { profile, isProfileComplete } = useProfile();

  // Capture UTM params from URL on every route change
  // (only writes to localStorage if UTM params are present)
  useEffect(() => {
    captureUTMParams();
    trackPageView(window.location.origin + location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Fire a marketing attribution event when ?ref= is present in the URL.
  // This captures even anonymous/ghost visitors who never sign up — critical for
  // measuring QR code and marketing channel effectiveness.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      trackEvent('marketing_landing', {
        ref_source: ref,
        landing_path: location.pathname,
      });
    }
  }, [location.pathname, location.search]);

  // Identify user when logged in (include UTM attribution), reset when logged out
  useEffect(() => {
    if (user) {
      const utm = getStoredUTMParams();
      identifyUser(user.id, {
        email: user.email,
        created_at: user.created_at,
        role: profile.accountRole || 'student',
        grade_level: profile.gradeLevel || undefined,
        state: profile.state || undefined,
        has_completed_profile: isProfileComplete,
        major_category: profile.majorCategory || undefined,
        ...utm,
      });
    } else {
      resetUser();
    }
  }, [user, profile, isProfileComplete]);

  // Track profile completion as a key event
  useEffect(() => {
    if (user && isProfileComplete) {
      trackEvent('profile_completed', {
        role: profile.accountRole || 'student',
        grade_level: profile.gradeLevel,
        state: profile.state,
      });
    }
  }, [user, isProfileComplete]);

  return null;
}
